import { analyticsAllowed } from './lib/consent';

function isFbPixelPresent() {
  return eval(import.meta.env.VITE_HAS_FB_PIXEL) && typeof window.fbq !== 'undefined'; // eslint-disable-line
}

function isBingPresent() {
  return eval(import.meta.env.VITE_HAS_BING_UET) && typeof window.uetq !== 'undefined'; // eslint-disable-line
}

// Save referrer
if (document.referrer !== '') {
  if (
    !(document.referrer.includes('paypal.com') || document.referrer.includes('epayworlwide.com'))
  ) {
    window.sessionStorage.setItem('referrer', document.referrer);
  }
}

const CATEGORIES = {
  ticket: 'Ticket',
  guidedTour: 'GuidedTour',
  merchandise: 'Merchandise',
  voucher: 'Voucher',
  annualPass: 'AnnualPass',
};

function toGa4Item(p, category) {
  const quantity = p.totalCount || p.count || 1;
  let unit = parseInt(p.price, 10);
  if (!unit || Number.isNaN(unit)) {
    const line = parseInt(p.totalPrice, 10) || 0;
    unit = quantity > 0 ? Math.round(line / quantity) : line;
  }
  return {
    item_id: String(p.id),
    item_name: p.name,
    item_category: category,
    price: unit / 100,
    quantity,
  };
}

function lineValue(p) {
  const line = parseInt(p.totalPrice, 10);
  if (!Number.isNaN(line)) {
    return line / 100;
  }
  const item = toGa4Item(p, '');
  return item.price * item.quantity;
}

function cartToItems(cart) {
  return cart.filter((p) => CATEGORIES[p.type]).map((p) => toGa4Item(p, CATEGORIES[p.type]));
}

function trackedPageLocation() {
  const url = new URL(window.location.href);
  ['consent_analytics', 'consent_source', 'gtm_debug'].forEach((param) => {
    url.searchParams.delete(param);
  });
  return url;
}

let lastPageLocation = null;

function pushEcommerce(event, ecommerce) {
  if (!analyticsAllowed()) {
    return;
  }
  if (typeof window.vue.$gtm === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ecommerce });
}

function addToCart(p, category) {
  pushEcommerce('add_to_cart', {
    currency: import.meta.env.VITE_CURRENCY,
    value: lineValue(p),
    items: [toGa4Item(p, category)],
  });
}

function removeFromCart(p, category) {
  pushEcommerce('remove_from_cart', {
    currency: import.meta.env.VITE_CURRENCY,
    value: lineValue(p),
    items: [toGa4Item(p, category)],
  });
}

const analytics = {
  addTicketToCart(p) {
    addToCart(p, 'Ticket');
    if (isFbPixelPresent()) {
      window.fbq('track', 'AddToCart', {
        content_ids: [p.id],
        content_name: p.name,
      });
    }
  },
  removeTicketFromCart(p) {
    removeFromCart(p, 'Ticket');
  },
  addGuidedTourToCart(p) {
    addToCart(p, 'GuidedTour');
  },
  removeGuidedTourFromCart(p) {
    removeFromCart(p, 'GuidedTour');
  },
  addMerchandiseToCart(p) {
    addToCart(p, 'Merchandise');
  },
  removeMerchandiseFromCart(p) {
    removeFromCart(p, 'Merchandise');
  },
  addAnnualPassToCart(p) {
    addToCart(p, 'AnnualPass');
  },
  removeAnnualPassFromCart(p) {
    removeFromCart(p, 'AnnualPass');
  },
  addVoucherToCart(p) {
    addToCart(p, 'Voucher');
  },
  removeVoucherFromCart(p) {
    removeFromCart(p, 'Voucher');
  },

  viewCart(cart, total) {
    const items = cartToItems(cart);
    if (items.length === 0) {
      return;
    }
    pushEcommerce('view_cart', {
      currency: import.meta.env.VITE_CURRENCY,
      value: parseInt(total, 10) / 100,
      items,
    });
  },
  beginCheckout(cart, total) {
    const items = cartToItems(cart);
    if (items.length === 0) {
      return;
    }
    pushEcommerce('begin_checkout', {
      currency: import.meta.env.VITE_CURRENCY,
      value: parseInt(total, 10) / 100,
      items,
    });
    if (isFbPixelPresent()) {
      window.fbq('track', 'InitiateCheckout');
    }
  },
  addPaymentInfo(cart, total, paymentType) {
    if (!paymentType) {
      return;
    }
    pushEcommerce('add_payment_info', {
      currency: import.meta.env.VITE_CURRENCY,
      value: parseInt(total, 10) / 100,
      payment_type: paymentType,
      items: cartToItems(cart),
    });
  },
  makePurchase(cart, cartId, total, tax) {
    pushEcommerce('purchase', {
      currency: import.meta.env.VITE_CURRENCY,
      transaction_id: String(cartId),
      value: parseInt(total, 10) / 100,
      tax: parseFloat(tax) || 0,
      items: cartToItems(cart),
    });
    if (isFbPixelPresent()) {
      const pIds = [];
      cart.forEach((p) => {
        pIds.push(p.id);
      });
      window.fbq('track', 'Purchase', {
        value: (parseInt(total, 10) / 100).toFixed(2),
        currency: import.meta.env.VITE_CURRENCY,
        content_ids: pIds,
        content_name: 'Tickets',
      });
    }
    if (isBingPresent()) {
      window.uetq.push('event', 'Purchase', {
        event_category: 'Ticketshop',
        event_label: 'Ticket',
        event_value: cart.length,
        revenue_value: (parseInt(total, 10) / 100).toFixed(2),
        currency: import.meta.env.VITE_CURRENCY,
      });
    }
  },
  pageView() {
    if (!analyticsAllowed()) {
      return;
    }
    if (typeof window.vue.$gtm === 'undefined') {
      return;
    }
    const url = trackedPageLocation();
    lastPageLocation = url.toString();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'page_view',
      page_path: url.pathname,
      page_title: document.title,
      page_location: url.toString(),
    });
  },
  syncTagManager() {
    const gtm = window.vue?.$gtm;
    if (!gtm || typeof gtm.enable !== 'function') {
      return;
    }
    if (analyticsAllowed()) {
      if (!gtm.enabled()) {
        gtm.enable(true);
        this.pageViewIfMissed();
      }
    } else if (gtm.enabled()) {
      gtm.enable(false);
    }
  },
  pageViewIfMissed() {
    if (lastPageLocation === trackedPageLocation().toString()) {
      return;
    }
    this.pageView();
  },
};

export default analytics;
